import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApp } from '../contexts/AppContext.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { apiUrl } from '../lib/config.js';
import { supabase } from '../lib/supabase.js';

export function useProfile() {
  const { session, user, loading: authLoading } = useAuth();
  const { interviewee, loading: appLoading, refreshInterviewee } = useApp();
  const [profile, setProfile] = useState(interviewee ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!user?.id) {
      setProfile(null);
      return null;
    }

    setLoading(true);
    setError('');

    try {
      const { data, error: fetchError } = await supabase
        .from('interviewees')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      setProfile(data ?? null);
      return data ?? null;
    } catch (err) {
      console.error('useProfile fetchProfile error', err);
      setError(err?.message || 'Could not load your profile.');
      setProfile(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (interviewee) {
      setProfile(interviewee);
      return;
    }

    if (!authLoading && !appLoading) {
      fetchProfile();
    }
  }, [authLoading, appLoading, interviewee, fetchProfile]);

  const updateProfile = useCallback(
    async (changes) => {
      if (!changes || Object.keys(changes).length === 0) return profile;

      setSaving(true);
      setError('');

      try {
        const {
          data: { session: liveSession },
        } = await supabase.auth.getSession();
        const token = liveSession?.access_token || session?.access_token;

        if (!token) {
          throw new Error('Your sign-in expired. Please sign in again.');
        }

        const response = await fetch(apiUrl('/api/v1/participant/me'), {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(changes),
        });

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          if (response.status === 404 || payload?.error === 'no_interviewee_row') {
            throw new Error('Finish onboarding before editing your profile.');
          }
          throw new Error(payload?.message || payload?.error || 'Could not save your profile.');
        }

        const nextProfile = payload?.interviewee ?? null;
        if (nextProfile) setProfile(nextProfile);
        await refreshInterviewee();
        return nextProfile;
      } catch (err) {
        console.error('useProfile updateProfile error', err);
        setError(err?.message || 'Could not save your profile.');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [profile, refreshInterviewee, session?.access_token],
  );

  const value = useMemo(
    () => ({
      user,
      profile,
      loading: authLoading || appLoading || loading,
      error,
      saving,
      refreshProfile: fetchProfile,
      updateProfile,
    }),
    [user, profile, authLoading, appLoading, loading, error, saving, fetchProfile, updateProfile],
  );

  return value;
}
